<template>
    <section class="grid-card3-section">
        <div class="grid-card3-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />

            <div class="grid-card3__grid">
                <article v-for="(cell, idx) in props.item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="grid-card3__item"
                    :style="getCellStyle(cell)" @click="handleCellClick(cell)">
                    <div class="grid-card3__media">
                        <MediaCard class="img" :url="getCellImage(cell)" :name="getCellTitle(cell)" />

                        <div v-if="getCellTitle(cell)" class="grid-card3__label">
                            {{ getCellTitle(cell) }}
                        </div>
                    </div>
                </article>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, getNumWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

/** 移动端最大列数 */
const MOBILE_COLS = 2
/** 桌面端最大列数 */
const DESKTOP_COLS = 3

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}
function getCellCol(cell: unknown): number {
    return getNumWithRecord(cell, 'col', 1)
}
function getCellRow(cell: unknown): number {
    return getNumWithRecord(cell, 'row', 1)
}

/**
 * 根据 col/row 计算内联样式
 * - 移动端最大跨2列，桌面端最大跨3列
 * - aspect-ratio = colSpan / rowSpan，保持网格单元格正方形为基准
 */
function getCellStyle(cell: unknown): Record<string, string> {
    const col = getCellCol(cell)
    const row = getCellRow(cell)
    const colMobile = Math.min(col, MOBILE_COLS)
    const colDesktop = Math.min(col, DESKTOP_COLS)
    return {
        '--card-col-mobile': String(colMobile),
        '--card-col-desktop': String(colDesktop),
        '--card-row-span': String(row),
        '--card-ratio-mobile': `${colMobile} / ${row}`,
        '--card-ratio-desktop': `${colDesktop} / ${row}`,
    } as Record<string, string>
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) {
        jumpToUrl(url)
    }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.grid-card3-section {
    width: 100%;
}

.grid-card3-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.grid-card3__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    grid-auto-flow: dense;

    @media (min-width: $breakpoint-md) {
        grid-template-columns: repeat(3, 1fr);
        gap: 1.25rem;
    }
}

.grid-card3__item {
    border-radius: 0.75rem;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    /* 移动端跨列/跨行 + 宽高比 */
    grid-column: span var(--card-col-mobile, 1);
    grid-row: span var(--card-row-span, 1);
    aspect-ratio: var(--card-ratio-mobile, 1 / 1);

    @media (min-width: $breakpoint-md) {
        /* 桌面端跨列/跨行 + 宽高比 */
        grid-column: span var(--card-col-desktop, 1);
        aspect-ratio: var(--card-ratio-desktop, 1 / 1);
        border-radius: 1rem;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }
}

.grid-card3__media {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: $color-bg-tertiary;

    .img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.grid-card3__label {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 2rem 1rem 0.75rem;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%);
    color: #fff;
    font-size: 0.875rem;
    font-weight: 700;
    text-align: center;
    z-index: 1;

    @media (min-width: $breakpoint-md) {
        font-size: 1.125rem;
        padding: 2.5rem 1.5rem 1rem;
    }
}
</style>
